import {
  parseQueryAttributeFilters,
  serializeQueryAttributeFilters,
} from '@components/modules/ProductGrid/Filter/filtering'
import { CustomProductGridTile } from '@features/products/api/get-custom-product-grid-tiles'
import { CustomProductGridTileVariantFragment } from '@lib/graphql/graphql'
import { isAvailableForPurchase } from '@lib/is-available-for-purchase'
import { mapEdgesToItems } from '@utils/map'
import { useQueryState } from 'next-usequerystate'
import { useMemo } from 'react'

type SortedProductGridTileVariant = CustomProductGridTileVariantFragment & {
  colorGroup: {
    name: string
    attributeValue: string
    color: string
  }
}
export type SortedCustomProductGridTile = Omit<
  CustomProductGridTile,
  'variants'
> & {
  variants: SortedProductGridTileVariant[]
  heroVariant?: SortedProductGridTileVariant
}

//@ts-ignore
export const useSortedProducts: (
  products: CustomProductGridTile[],
  removeQueryFilter: (slug: string, value: string) => Promise<URLSearchParams>,
  colorSorting: {
    name: string
    hex: string
  }[],
  productSorting?: boolean,
  wishlistMode?: boolean,
  componentPriority?: string[],
) => {
  sortedProducts: SortedCustomProductGridTile[]
} = (
  products,
  removeQueryFilter,
  colorSorting,
  productSorting,
  wishlistMode,
  componentPriority,
) => {
  const [queryFilters, setQueryFilters] = useQueryState('filters', {
    parse: parseQueryAttributeFilters,
    serialize: serializeQueryAttributeFilters,
    defaultValue: [],
  })

  const sortedProducts = useMemo(() => {
    if (wishlistMode === undefined || wishlistMode === true) return []

    const allVariants = products.flatMap((parent) =>
      mapEdgesToItems(parent.variants).map((variant) => ({
        ...variant,
        parent: { ...parent, variants: [] },
      })),
    )

    if (productSorting === true)
      return products
        .filter((product) => (product.variants?.edges?.length ?? 0) > 0)
        .flatMap((parent) => {
          const variants = mapEdgesToItems(parent.variants)

          const qualifiedVariants: typeof variants | undefined =
            variants.filter((variant) =>
              isAvailableForPurchase(variant?.availability),
            )

          let qualifiedVariant: (typeof qualifiedVariants)[0] | undefined
          if (qualifiedVariants.length == 0) {
            qualifiedVariant = variants[0]
          } else {
            // find the variant with highest priority in "bestSellers"
            qualifiedVariant = qualifiedVariants[0]
          }

          return {
            ...parent,
            variants: [qualifiedVariant],
          }
        })
        .sort((a, b) => {
          // if contained in component priority, then that takes precedence
          const aPriority = componentPriority?.indexOf(a.id) ?? -1
          const bPriority = componentPriority?.indexOf(b.id) ?? -1

          if (aPriority !== -1 && bPriority !== -1) {
            return aPriority - bPriority
          }
          if (aPriority !== -1) {
            return -1
          }
          if (bPriority !== -1) {
            return 1
          }

          // Direct product sorting (Specified by CMS!) Overrules everything

          // Available to purchase comes FIRST always
          const aAvailable = isAvailableForPurchase(a.variants[0]?.availability)
          const bAvailable = isAvailableForPurchase(b.variants[0]?.availability)

          if (aAvailable && !bAvailable) return -1
          if (!aAvailable && bAvailable) return 1

          // Sort by best sellers
          // const aBestSeller = bestSellers?.find(
          //   (bestSeller) => bestSeller.sku === a.variants[0].sku,
          // )
          // const bBestSeller = bestSellers?.find(
          //   (bestSeller) => bestSeller.sku === b.variants[0].sku,
          // )

          // // higher priority is better
          // if (aBestSeller && bBestSeller) {
          //   return bBestSeller.priority - aBestSeller.priority
          // }

          //find the bestseller_no key in product metadata, should be sorted by low to high, so 0 should come before 1
          const aBestsellerNo = a.metadata.find(
            (x) => x.key === 'bestseller_no',
          )?.value
          const bBestsellerNo = b.metadata.find(
            (x) => x.key === 'bestseller_no',
          )?.value

          if (aBestsellerNo && bBestsellerNo) {
            return parseInt(aBestsellerNo) - parseInt(bBestsellerNo)
          }

          return 0
        })

    const attributeValueLookup = products.reduce(
      (acc, product) => {
        product.attributeAssignments.forEach((attr) => {
          mapEdgesToItems(attr.values).forEach((value) => {
            const colorGroup = value.metadata?.find(
              (x) => x.key === 'COLOR_GROUP',
            )?.value
            if (colorGroup) {
              const key = `${attr.name}:${value.value}`
              acc[key] = {
                colorGroup,
                attributeValue: value.value,
                color: value.metadata.find(
                  (x) => x.key === 'FABRIC_COLOR_GROUP_HEX',
                )?.value,
              }
            }
          })
        })
        return acc
      },
      {} as Record<
        string,
        { colorGroup: string; attributeValue: string; color: string }
      >,
    )

    const colorGroups = products.reduce(
      (acc, product) => {
        product.attributeAssignments.forEach((attr) => {
          mapEdgesToItems(attr.values).forEach((value) => {
            const colorGroup = value.metadata?.find(
              (x) => x.key === 'COLOR_GROUP',
            )?.value
            if (colorGroup) {
              acc[colorGroup] = []
            }
          })
        })
        return acc
      },
      {} as Record<string, SortedProductGridTileVariant[]>,
    )

    allVariants.forEach((variant) => {
      const flatAttributeValueArray = variant.selectedAttributes
        .filter((x) => {
          const key = `${x.name}:${x.value}`
          return attributeValueLookup[key]
        })
        .sort((a, b) => {
          const sortByNames = ['color', 'colour', 'shade', 'uphol']

          const aName = a.name.toLowerCase()
          const bName = b.name.toLowerCase()

          let aIndex = sortByNames.findIndex((x) => aName.includes(x))
          let bIndex = sortByNames.findIndex((x) => bName.includes(x))

          if (aIndex == -1) aIndex = 100
          if (bIndex == -1) bIndex = 100

          if (aIndex > bIndex) {
            return 1
          }
          if (aIndex < bIndex) {
            return -1
          }
          return 0
        })

      const firstColorAttributeValue = flatAttributeValueArray.find(
        (x) => !!x.value,
      )

      if (!firstColorAttributeValue) {
        // console.log('No color attribute found for variant', variant)
      }

      const key = `${firstColorAttributeValue?.name}:${firstColorAttributeValue?.value}`
      const attrValLookup = attributeValueLookup[key]

      if (!attrValLookup) {
        return
      }

      const variantToPush = structuredClone({
        colorGroup: {
          name: attrValLookup.colorGroup,
          attributeValue: attrValLookup.attributeValue,
          color: attrValLookup.color,
        },
        ...variant,
      })

      colorGroups[attrValLookup.colorGroup].push(variantToPush)
    })

    console.log(Object.keys(colorGroups)[0])
    const filteredProducts = Object.keys(colorGroups)
      .sort((a, b) => {
        //sor the color groups by the colorSorting array
        const aIndex = colorSorting.findIndex((x) => x.name === a)
        const bIndex = colorSorting.findIndex((x) => x.name === b)
        if (aIndex === -1 && bIndex === -1) {
          return a.localeCompare(b)
        }
        if (aIndex === -1) {
          return 1
        }
        if (bIndex === -1) {
          return -1
        }
        return aIndex - bIndex
      })
      .flatMap((key) => {
        const variants = colorGroups[key]
          .sort((a, b) => {
            const brightnessA = getBrightness(a.colorGroup.color)
            const brightnessB = getBrightness(b.colorGroup.color)
            return brightnessB - brightnessA // Sort from brightest to darkest
          })
          .map((variant) => {
            //find the parent product for the variant
            const parent = products.find(
              (product) => product.id === (variant as any).parent.id,
            )
            return {
              ...parent,
              variants: [variant],
            }
          })
        return variants
      })

    queryFilters.forEach((filter) => {
      if (filter.slug === 'color') {
        //make sure the color is in some of the products
        filter.values.forEach((color) => {
          const productWithColorGroupExits = filteredProducts.some((product) =>
            product.variants.some(
              (variant) =>
                variant?.colorGroup?.name?.toLowerCase() ===
                color?.toLowerCase(),
            ),
          )

          if (!productWithColorGroupExits) {
            removeQueryFilter('color', color)
          }
        })
      }
    })

    return filteredProducts
  }, [products, wishlistMode, productSorting, componentPriority, queryFilters])

  return { sortedProducts }
}

function getBrightness(hex: string) {
  if (!hex) return -1
  const hexCode = hex.replace('#', '')
  const r = parseInt(hexCode.substring(0, 2), 16)
  const g = parseInt(hexCode.substring(2, 4), 16)
  const b = parseInt(hexCode.substring(4, 6), 16)
  return Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b))
}
